export type TemplateType = 'EMAIL' | 'SMS' | 'PUSH';

export enum TemplateDetailTabs {
  DETAILS = 0,
  CHANGELOG = 1,
}

export interface ApiErrorResponse {
  code: string;
  message: string;
  error: string;
}

export enum TemplateErrorCode {
  TEMPLATE_NOT_FOUND = 'TMPL_001',
  TEMPLATE_ALREADY_EXISTS = 'TMPL_002',
  TEMPLATE_VERSION_NOT_FOUND = 'TMPL_003',
  TEMPLATE_INVALID_SUFFIX = 'TMPL_004',
  TEMPLATE_EXPORT_ERROR = 'TMPL_005',
  TEMPLATE_VALIDATION_ERROR = 'TMPL_006',
  TEMPLATE_INTERNAL_SERVER_ERROR = 'TMPL_500',
}

export const extractApiError = (error: any): ApiErrorResponse | null => {
  const data = error?.response?.data;
  if (!data) return null;

  // Caso 1: errore strutturato restituito direttamente { code, message, error }
  // (es. chiamata diretta al notification-hub-service).
  if (data.code && data.message) {
    return {
      code: data.code,
      message: data.message,
      error: data.error || '',
    };
  }

  // Caso 2: errore incapsulato dal system-console-backend (ExceptionHelper -> ErrorResponse).
  // Il proxy, sui 4xx/5xx del microservizio, rilancia HttpClientErrorException e l'advice
  // re-impacchetta il body originale dentro `errorDetails`. Per il notification-hub questo
  // body è l'oggetto JSON { code, message, error }, tipicamente serializzato come stringa.
  // (Lo stesso campo `errorDetails` è quello letto dagli altri stream della console.)
  if (data.errorDetails != null) {
    let details: any = data.errorDetails;
    if (typeof details === 'string') {
      try {
        details = JSON.parse(details);
      } catch {
        details = null;
      }
    }
    if (details && details.code && details.message) {
      return {
        code: details.code,
        message: details.message,
        error: details.error || '',
      };
    }
  }

  return null;
};

export const getErrorMessage = (errorCode: string, t: (key: string) => string): string => {
  const errorMap: Record<string, string> = {
    [TemplateErrorCode.TEMPLATE_NOT_FOUND]: t('templates:errors.templateNotFound'),
    [TemplateErrorCode.TEMPLATE_ALREADY_EXISTS]: t('templates:errors.templateAlreadyExists'),
    [TemplateErrorCode.TEMPLATE_VERSION_NOT_FOUND]: t('templates:errors.versionNotFound'),
    [TemplateErrorCode.TEMPLATE_INVALID_SUFFIX]: t('templates:errors.invalidSuffix'),
    [TemplateErrorCode.TEMPLATE_EXPORT_ERROR]: t('templates:errors.exportError'),
    [TemplateErrorCode.TEMPLATE_VALIDATION_ERROR]: t('templates:errors.validationError'),
    [TemplateErrorCode.TEMPLATE_INTERNAL_SERVER_ERROR]: t('templates:errors.internalError'),
  };
  return errorMap[errorCode] || t('templates:errors.serverError');
};

export interface Template {
  id?: string;
  name: string;
  tenantId?: string;
  type: TemplateType;
  status?: string;
  createdBy?: string;
  createdAt?: string;
  updatedBy?: string;
  updatedAt?: string;
  versions?: TemplateVersion[];
}

export interface TemplateVersion {
  id?: number;
  templateId?: number;
  versionNumber: number;
  isActive: boolean;
  subjectTitle?: string;
  body?: string;
  description?: string;
  createdBy?: string;
  createdAt?: string;
  updatedBy?: string;
  updatedAt?: string;
}

export interface PaginatedResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  page: number;
  size: number;
  first: boolean;
  last: boolean;
}

export interface TemplateResult {
  templateName: string;
  code: string;
  errorMessage?: string;
}

export interface CreateVersionRequest {
  subjectTitle?: string;
  body: string;
  description?: string;
}

export interface CreateTemplateRequest {
  name: string;
  type: TemplateType;
  tenantId?: string;
  createdBy: string;
  versions: CreateVersionRequest[];
}

export interface UpdateVersionRequest {
  versionNumber?: number;
  subjectTitle?: string;
  body: string;
  description?: string;
}

export interface UpdateTemplateRequest {
  name: string;
  newName?: string;
  updatedBy?: string;
  versions: UpdateVersionRequest[];
}

export interface GetTemplatesParams {
  tenantId: string[];
  bodyType?: string;
  page?: number;
  size?: number;
  sortBy?: string;
  sortDirection?: string;
  type?: string;
  nameTokens?: string[];
  nameOperator?: string;
  withTemplateVersions?: boolean;
}

// --- Tenant utilities ---

const TENANT_MAP: Record<string, string> = {
  it: 'drivalia_it',
  es: 'drivalia_es',
  fr: 'drivalia_fr',
  pt: 'drivalia_pt',
  en: 'drivalia_it',  // Template in inglese → drivalia_it
};
const DEFAULT_TENANT = 'drivalia_it';
export const VALID_COUNTRY_SUFFIXES = ['it', 'es', 'fr', 'pt', 'en'];
export const ALL_TENANTS = ['drivalia_it', 'drivalia_es', 'drivalia_fr', 'drivalia_pt'];

/** Deduce il tenantId dal nome del template */
export const resolveTenantIdFromName = (templateName: string): string => {
  if (!templateName || templateName.length < 2) return DEFAULT_TENANT;
  const suffix = templateName.slice(-2).toLowerCase();
  return TENANT_MAP[suffix] || DEFAULT_TENANT;
};

/** Restituisce i tenantId dell'utente per la ricerca */
export const getUserTenantIds = (user: any): string[] => {
  if (!user?.legalEntities?.length) return [DEFAULT_TENANT];
  return user.legalEntities.map((le: any) => le.name);
};

/** True se l'utente ha più di un tenant */
export const isMultiTenant = (user: any): boolean => {
  return (user?.legalEntities?.length ?? 0) > 1;
};

/** Restituisce tutti i tenantId disponibili per visualizzare tutti i template */
export const getAllTenantIds = (): string[] => {
  return ALL_TENANTS;
};


export interface AuditEntry {
  id: number;
  templateId: number;
  templateName?: string;
  entityType: string;
  action: string;
  oldValue?: string;
  newValue?: string;
  changedBy: string;
  changedAt: string;
  field?: string;
  versionId?: number;
  versionNumber?: number;
  transactionId?: string;
}

export type TemplateHelperCategory = 'conditional' | 'standard' | 'string';
export type TemplateHelperType = 'helper' | 'placeholder';

export interface TemplateHelperMetadata {
  class: string;
  category: TemplateHelperCategory;
  startKey: string;
  endKey: string | null;
  type: TemplateHelperType;
}
